import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const axiosInstance = axios.create({ withCredentials: true });
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const ManageSubscription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    loadSubscriptionInfo();
    loadInvoices();
    checkPaymentReturn();
  }, []);

  const loadSubscriptionInfo = async () => {
    try {
      const response = await axiosInstance.get(`${API}/subscription/status`);
      setSubscriptionInfo(response.data);
    } catch (error) {
      toast.error('Failed to load subscription info');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      const response = await axiosInstance.get(`${API}/subscription/invoices`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to load invoices:', error);
    }
  };

  const checkPaymentReturn = async () => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const canceled = params.get('canceled');
    if (canceled) { toast.error('Payment canceled'); window.history.replaceState({}, '', '/manage-subscription'); return; }
    if (sessionId) { setProcessing(true); await pollPaymentStatus(sessionId); }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) { toast.error('Payment verification timed out. Please refresh the page.'); setProcessing(false); return; }
    try {
      const response = await axiosInstance.get(`${API}/subscription/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        window.history.replaceState({}, '', '/manage-subscription');
        await loadSubscriptionInfo();
        setProcessing(false);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setProcessing(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      toast.error('Error checking payment status');
      setProcessing(false);
    }
  };

  const handleSubscribe = async (packageId) => {
    setSelectedPlan(packageId);
    setProcessing(true);
    try {
      const response = await axiosInstance.post(`${API}/subscription/checkout`, { package_id: packageId, origin_url: window.location.origin });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to initiate payment');
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) return;
    try {
      await axiosInstance.post(`${API}/subscription/cancel`);
      toast.success('Subscription cancelled. Access continues until expiration.');
      await loadSubscriptionInfo();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  if (loading || processing) {
    return <div className="loading-spinner" data-testid="loading-spinner">{processing ? 'Processing payment...' : 'Loading...'}</div>;
  }

  return (
    <div className="dashboard-container" data-testid="manage-subscription">
      <Toaster position="top-right" richColors />
      <div className="dashboard-header">
        <div className="logo"><span className="brand-cx">CX</span><span className="brand-slash">/</span><span className="brand-recon">recon</span></div>
        <button className="back-btn" onClick={() => navigate('/dashboard')} data-testid="back-button">← Back to Dashboard</button>
      </div>

      <div className="subscription-content">
        <h1 className="dashboard-title">Manage Subscription</h1>

        <div className="current-plan-card">
          <h2>Current Plan</h2>
          {subscriptionInfo && (
            <div className="plan-info">
              <div className="plan-status">
                {(subscriptionInfo.subscription_status === 'free' || subscriptionInfo.subscription_status === 'recruit') ? (
                  <span className="status-badge free">🎖️ Recruit (Free)</span>
                ) : (
                  <span className="status-badge pro">
                    {subscriptionInfo.subscription_status === 'sergeant' || subscriptionInfo.subscription_status === 'monthly' ? '⭐ Sergeant' 
                      : subscriptionInfo.subscription_status === 'lieutenant' || subscriptionInfo.subscription_status === 'yearly' ? '🏅 Lieutenant' : 'Pro'}
                  </span>
                )}
              </div>
              {(subscriptionInfo.subscription_status === 'free' || subscriptionInfo.subscription_status === 'recruit') && (
                <div className="free-plan-info"><p>Surveys used: {subscriptionInfo.survey_count} / 3</p><p>Surveys remaining: {subscriptionInfo.surveys_remaining}</p></div>
              )}
              {subscriptionInfo.has_active_subscription && (
                <div className="subscription-details">
                  {subscriptionInfo.subscription_expires_at ? (
                    <p>Renews: {new Date(subscriptionInfo.subscription_expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  ) : (
                    <p>Status: Active (recurring)</p>
                  )}
                  <button className="cancel-btn" onClick={handleCancelSubscription}>Cancel Subscription</button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Upgrade Section for Free Users */}
        {subscriptionInfo && (subscriptionInfo.subscription_status === 'free' || subscriptionInfo.subscription_status === 'recruit') && (
          <div className="pricing-section">
            <h2>Upgrade to Pro</h2>
            <div className="pricing-cards">
              <div className="pricing-card">
                <h3>⭐ Sergeant</h3>
                <p className="plan-subtitle">Monthly Plan</p>
                <div className="price"><span className="amount">$10</span><span className="period">/month</span></div>
                <ul className="features">
                  <li>✓ Unlimited Surveys</li>
                  <li>✓ Up to 25 Questions per Survey</li>
                  <li>✓ AI Survey Generation</li>
                  <li>✓ AI Summary Reports</li>
                  <li>✓ CSV Export</li>
                  <li>✓ Advanced Analytics</li>
                  <li>✓ Company Branding</li>
                </ul>
                <button className="upgrade-btn" onClick={() => handleSubscribe('sergeant')} disabled={selectedPlan === 'sergeant'} data-testid="subscribe-sergeant">
                  {selectedPlan === 'sergeant' ? 'Processing...' : 'Subscribe Monthly'}
                </button>
              </div>
              <div className="pricing-card featured">
                <div className="best-value-badge">EARLY ADOPTER - 40% OFF</div>
                <h3>🏅 Lieutenant</h3>
                <p className="plan-subtitle">Yearly Plan</p>
                <div className="price"><span className="original-price">$95</span><span className="amount">$57</span><span className="period">/year</span></div>
                <p className="savings">Save $63/year + Early Adopter Bonus!</p>
                <ul className="features">
                  <li>✓ Unlimited Surveys</li>
                  <li>✓ Up to 50 Questions per Survey</li>
                  <li>✓ AI Survey Generation</li>
                  <li>✓ AI Summary Reports</li>
                  <li>✓ CSV Export</li>
                  <li>✓ Advanced Analytics</li>
                  <li>✓ Company Branding</li>
                  <li>✓ Priority Support</li>
                </ul>
                <button className="upgrade-btn featured" onClick={() => handleSubscribe('lieutenant')} disabled={selectedPlan === 'lieutenant'} data-testid="subscribe-lieutenant">
                  {selectedPlan === 'lieutenant' ? 'Processing...' : 'Subscribe Yearly'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Section for Monthly/Sergeant Users */}
        {subscriptionInfo && (subscriptionInfo.subscription_status === 'monthly' || subscriptionInfo.subscription_status === 'sergeant') && (
          <div className="pricing-section upgrade-section">
            <h2>🚀 Upgrade Your Plan</h2>
            <p className="upgrade-description">Get more features and save money by upgrading to our yearly plan!</p>
            <div className="pricing-cards">
              <div className="pricing-card current-plan">
                <div className="current-badge">CURRENT PLAN</div>
                <h3>⭐ Sergeant</h3>
                <p className="plan-subtitle">Monthly Plan</p>
                <div className="price"><span className="amount">$10</span><span className="period">/month</span></div>
                <ul className="features">
                  <li>✓ Unlimited Surveys</li>
                  <li>✓ Up to 25 Questions per Survey</li>
                  <li>✓ AI Survey Generation</li>
                  <li>✓ AI Summary Reports</li>
                  <li>✓ Company Branding</li>
                </ul>
                <button className="upgrade-btn disabled" disabled>Current Plan</button>
              </div>
              <div className="pricing-card featured">
                <div className="best-value-badge">UPGRADE & SAVE 52%</div>
                <h3>🏅 Lieutenant</h3>
                <p className="plan-subtitle">Yearly Plan</p>
                <div className="price"><span className="original-price">$120/yr</span><span className="amount">$57</span><span className="period">/year</span></div>
                <p className="savings">Save $63/year vs Monthly!</p>
                <ul className="features">
                  <li>✓ Everything in Sergeant, plus:</li>
                  <li>✓ Up to 50 Questions per Survey</li>
                  <li>✓ Priority Support</li>
                  <li>✓ Early Adopter Pricing Locked In</li>
                </ul>
                <button className="upgrade-btn featured" onClick={() => handleSubscribe('lieutenant')} disabled={selectedPlan === 'lieutenant'} data-testid="upgrade-to-lieutenant">
                  {selectedPlan === 'lieutenant' ? 'Processing...' : '⬆️ Upgrade to Lieutenant'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Already on Yearly - Show Thank You */}
        {subscriptionInfo && (subscriptionInfo.subscription_status === 'yearly' || subscriptionInfo.subscription_status === 'lieutenant') && (
          <div className="pricing-section thank-you-section">
            <div className="thank-you-card">
              <h2>🏅 You're on our Best Plan!</h2>
              <p>Thank you for being a Lieutenant subscriber. You have access to all premium features.</p>
              <ul className="features-summary">
                <li>✓ Unlimited Surveys</li>
                <li>✓ Up to 50 Questions per Survey</li>
                <li>✓ AI Survey Generation & Summary Reports</li>
                <li>✓ Company Branding</li>
                <li>✓ Priority Support</li>
              </ul>
            </div>
          </div>
        )}

        {invoices.length > 0 && (
          <div className="invoices-section">
            <h2>Payment History</h2>
            <div className="invoices-table">
              <table>
                <thead><tr><th>Date</th><th>Plan</th><th>Amount</th><th>Status</th></tr></thead>
                <tbody>
                  {invoices.map((invoice, index) => (
                    <tr key={index}>
                      <td>{new Date(invoice.created_at).toLocaleDateString()}</td>
                      <td>{invoice.package_name || invoice.package_id}</td>
                      <td>${invoice.amount}</td>
                      <td><span className="status-badge paid">{invoice.payment_status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageSubscription;
